/**
 * Planned SFDC → ShelfCycle order-document modal bindings (agent/dev reference map).
 * Source: customer onboarding probes (NuOrder / line-item / product field matrices).
 *
 * SFDC object/field names live here for future wiring — never shown in the product UI.
 * Hint rule: prefilled modal fields → human ShelfCycle source in buildHintMap overrides when listed.
 *            other fields → uiHint or generic not-prefilled copy unless key is in SUPPRESS_UI_HINT_KEYS (no dimmed hint).
 */
export type OrderDocumentSfdcFieldRef = {
  /** SFDC object API name (dev reference only). */
  object: string;
  /** SFDC field API name (dev reference only). */
  field: string;
  /** Agent/onboarding notes — not shown in UI. */
  note?: string;
  /** Optional user-facing hint when the field is not prefilled from order data. */
  uiHint?: string;
};

const MANUAL_MODAL_FIELD_HINT = "Not prefilled from order data today.";

/** User-facing hint for modal fields without a ShelfCycle prefill source. */
export const formatSfdcOnlyHint = (ref: OrderDocumentSfdcFieldRef): string => ref.uiHint ?? MANUAL_MODAL_FIELD_HINT;

/** Border US modal keys → reference NuOrder / line / product fields. */
export const borderUsSfdcBindings = {
  approximateCrossingDate: {
    object: "NuOrder__c",
    field: "Approximate_Crossing_Date__c",
    note: "Saved crossing schedule — cover letter only; invoice date is an issuance field, sale date uses the order date.",
    uiHint: "Saved approximate crossing schedule for the cover letter.",
  },
  importCrossingLocation: {
    object: "NuOrder__c",
    field: "Import_Crossing_Location__c",
    note: "PDF Port of Entry (US import side)",
  },
  exportBorderCrossing: { object: "NuOrder__c", field: "Export_Border_Crossing__c", note: "PDF Border crossing point (export side)" },
  containerType: { object: "NuOrder__c", field: "Container_Type__c" },
  shipperConsigneeRef: {
    object: "NuOrder__c",
    field: "MOPS_Order__c",
    note: "alt Name (PO #) — ShelfCycle prefill uses SO/PO numbers",
  },
  exporter: {
    object: "NuOrder__c",
    field: "Supplier__c",
    note: "ship-from also Pick_Up_Location__c / Shipper__c",
  },
  buyer: { object: "NuOrder__c", field: "Company__c", note: "sold-to party; Contact__c for attn" },
  buyerFedId: {
    object: "Location__c",
    field: "Bill_to_Federal_Tax_Number__c",
    note: "alt Account.Federal_Tax_ID_Number__c",
  },
  importerOfRecord: {
    object: "—",
    field: "—",
    note: "Prefilled from this organization's profile",
  },
  operatingEntity: {
    object: "—",
    field: "—",
    note: "Prefilled from this organization's profile",
  },
  consignee: { object: "NuOrder__c", field: "Ship_To_Location__c" },
  countryOfOrigin: {
    object: "NuProduct__c",
    field: "Country_of_Origin__c",
    note: "Initial default from the document ship-from country; editable on create, not product-attribute wired",
  },
  destinationCountry: { object: "NuOrder__c", field: "Destination__c" },
  lineCountryOfManufacture: {
    object: "NuProduct__c",
    field: "Country_of_Origin__c",
    note: "wired via Border U.S. settings → Attribute wiring when configured",
  },
  authorizedBy: {
    object: "—",
    field: "—",
    note: "Prefilled from the ShelfCycle user opening the modal",
  },
  lineDescription: { object: "NuOrder_Line_Item__c", field: "Product__c", note: "via line product description" },
  lineHsNumber: {
    object: "NuProduct__c",
    field: "US_Tariff__c",
    note: "wired via Border U.S. settings → Attribute wiring when configured",
  },
  lineTariffTreatment: {
    object: "NuProduct__c",
    field: "Import_to_US_Duty__c",
    note: "Legacy Salesforce reference only; the built-in U.S. PDF has no tariff-treatment field",
  },
  lineQuantityDisplay: { object: "NuOrder_Line_Item__c", field: "Qty_Shipped_kgs__c", note: "alt Quantity_Shipped__c + units" },
  tscaCertification: {
    object: "NuProduct__c",
    field: "TSCA_US__c",
    note: "product regulatory flag only — not wired; built-in U.S. documents always certify compliance",
  },
} as const satisfies Record<string, OrderDocumentSfdcFieldRef>;

/** Border Canada modal keys → reference SFDC fields. */
export const borderCanadaSfdcBindings = {
  billTo: {
    object: "—",
    field: "—",
    note: "Prefilled from this organization's profile",
  },
  exporterLegalName: {
    object: "—",
    field: "—",
    note: "Prefilled from this organization's profile",
  },
  approximateCrossingDate: { object: "NuOrder__c", field: "Approximate_Crossing_Date__c" },
  exportBorderCrossing: { object: "NuOrder__c", field: "Export_Border_Crossing__c" },
  importCrossingLocation: { object: "NuOrder__c", field: "Import_Crossing_Location__c" },
  containerType: { object: "NuOrder__c", field: "Container_Type__c" },
  vendorName: { object: "NuOrder__c", field: "Supplier__c" },
  vendorAddress: { object: "NuOrder__c", field: "Supplier__c", note: "supplier account address block" },
  shipFromName: { object: "NuOrder__c", field: "Pick_Up_Location__c" },
  shipFromAddress: { object: "NuOrder__c", field: "Pick_Up_Location__c", note: "pick-up / ship-from location address" },
  deliverTo: { object: "NuOrder__c", field: "Ship_To_Location__c", note: "order ship-to address block" },
  countryOfOrigin: {
    object: "NuProduct__c",
    field: "Country_of_Origin__c",
    note: "Initial default from the document ship-from country; editable on create, not product-attribute wired",
  },
  lineDescription: { object: "NuOrder_Line_Item__c", field: "Product__c" },
  lineValueUsd: {
    object: "NuOrder_Line_Item__c",
    field: "Invoice_Amount_for_Reporting__c",
    note: "alt Selling_Price__c × quantity; USD conversion at PDF",
  },
  lineQuantityKg: { object: "NuOrder_Line_Item__c", field: "Qty_Shipped_kgs__c" },
  lineTariffNumber: {
    object: "NuProduct__c",
    field: "CA_Tariff__c",
    note: "wired via Border Canadian settings → Attribute wiring when configured",
  },
  lineDuty: {
    object: "NuProduct__c",
    field: "US_to_CA_Duty__c",
    note: "wired via Border Canadian settings → Attribute wiring when configured",
  },
} as const satisfies Record<string, OrderDocumentSfdcFieldRef>;

/** SC-2344 pickup-instructions modal (when shipped). */
export const pickupInstructionsSfdcBindings = {
  pickUpLocation: { object: "NuOrder__c", field: "Pick_Up_Location__c" },
  releaseNumber: { object: "NuOrder__c", field: "Release_Number__c", note: "alt ST_Release_Number__c" },
  pickUpNotes: { object: "NuOrder__c", field: "Pick_Up_Notes_For_Trucking_Instructions__c" },
  carrierRates: {
    object: "NuOrder__c",
    field: "Base_Rate__c",
    note:
      "SFDC combines base rate, fuel surcharge and extra charges in Trucking_Currency__c. " +
      "ShelfCycle prints the full description, total and currency from selected SO/PO costs.",
  },
  includeBlindPickupDelivery: { object: "NuOrder__c", field: "Blind_Pickup_Option__c" },
  carrier: { object: "NuOrder__c", field: "Carrier__c", note: "alt Carrier_Contact__c + Carrier_Description__c" },
  deliveryBlock: { object: "NuOrder__c", field: "Ship_To_Location__c" },
} as const satisfies Record<string, OrderDocumentSfdcFieldRef>;

/** SC-2343 BOL line modal fields (commodity suffixes). Emergency flags are org-configured in BOL_DOCUMENT_CONFIG.emergency.flags. */
export const bolSfdcBindings = {
  carrier: { object: "NuOrder__c", field: "Carrier__c" },
  lineCountryOfOrigin: {
    object: "NuProduct__c",
    field: "Country_of_Origin__c",
    note: "wired via Bill of Lading settings → Attribute wiring when modal slot enabled",
  },
  lineErgNumber: {
    object: "NuProduct__c",
    field: "ERG__c",
    note: "wired via Bill of Lading settings → Attribute wiring when modal slot enabled",
  },
  lineProducer: {
    object: "NuProduct__c",
    field: "Producer__c",
    note: "Uses the saved purchase-order line producer, not a catalog-attribute or modal override",
  },
  lineCommodityDescription: {
    object: "NuProduct__c",
    field: "UN_Number__c",
    note: "prefilled from product hazmat fields + product code when commodity-description slot enabled",
  },
  linePieces: { object: "NuOrder_Line_Item__c", field: "Quantity_Shipped__c", note: "BOL commodity Pieces column" },
} as const satisfies Record<string, OrderDocumentSfdcFieldRef>;

/** SC-2345 OC conditional page. */
export const orderConfirmationSfdcBindings = {
  includeDrumHazardPage: {
    object: "NuOrder__c",
    field: "Print_Drum_Hazard__c",
    note: "inverted in SF — unchecked on drum orders forces page 2",
  },
  orderConfirmationNote: { object: "NuOrder__c", field: "Order_Confirmation_Note__c" },
  lineProducer: {
    object: "NuOrder_Line_Item__c",
    field: "Producer__c",
    note: "alt NuProduct__c producer fields",
  },
  lineQuantityUnits: {
    object: "NuOrder_Line_Item__c",
    field: "Quantity_Shipped__c",
    note: "alt Qty_Shipped_kgs__c + units",
    uiHint: "Enter quantity and units for this line when order data is incomplete.",
  },
  lineDescription: { object: "NuOrder_Line_Item__c", field: "Product__c", note: "via line product description" },
} as const satisfies Record<string, OrderDocumentSfdcFieldRef>;

/** Fields prefilled today in border-us prefill — human-readable ShelfCycle sources only. */
const borderUsShelfCyclePrefillSources: Partial<Record<keyof typeof borderUsSfdcBindings, string>> = {
  exporter: "Enter or confirm the origin for this document; saved order addresses are available above.",
  buyer: "Enter or confirm the sold-to party for this document.",
  consignee: "Prefilled from the order destination; edit for this document if needed.",
  destinationCountry: "Prefilled from the order destination country when available.",
  shipperConsigneeRef: "Prefilled from the sales or purchase order reference; edit before create.",
  importerOfRecord: "Enter or confirm the importer of record for this document.",
  operatingEntity: "Prefilled from this organization's profile; edit before create.",
  lineDescription: "Prefilled from product data; refreshed from the order when the PDF is generated.",
  authorizedBy: "Enter or confirm the person and organization authorizing this document.",
};

/** Fields prefilled today in border-canada prefill — human-readable ShelfCycle sources only. */
const borderCanadaShelfCyclePrefillSources: Partial<Record<keyof typeof borderCanadaSfdcBindings, string>> = {
  billTo: "Prefilled from this organization's profile; edit before create.",
  exporterLegalName: "Enter or confirm the exporter legal name for this document.",
  vendorName: "Prefilled from the purchase order supplier when available.",
  vendorAddress: "Prefilled from the purchase order supplier address when available.",
  shipFromName: "Enter or confirm the origin for this document; saved order addresses are available above.",
  shipFromAddress: "Enter or confirm the origin address for this document; saved order addresses are available above.",
  deliverTo: "Prefilled from the order destination; choose a saved address or edit it for this document.",
  lineDescription: "Prefilled from product data; refreshed from the order when the PDF is generated.",
  lineQuantityKg: "Prefilled from order line net weight (converted to kg).",
};

/** Modal fields that should not show a dimmed prefill hint under the control. */
const SUPPRESS_UI_HINT_KEYS = new Set<string>([
  "countryOfOrigin",
  "lineCountryOfManufacture",
  "lineHsNumber",
  "lineTariffTreatment",
  "lineQuantityDisplay",
  "tscaCertification",
  "lineTariffNumber",
  "lineDuty",
  "lineValueUsd",
  "lineCountryOfOrigin",
  "lineErgNumber",
  "lineProducer",
]);

const buildHintMap = <T extends Record<string, OrderDocumentSfdcFieldRef>>(
  bindings: T,
  shelfCyclePrefillSources: Partial<Record<keyof T, string>>,
): { [K in keyof T]: string } => {
  const hints = {} as { [K in keyof T]: string };
  for (const key of Object.keys(bindings) as Array<keyof T>) {
    const shelfCycleSource = shelfCyclePrefillSources[key];
    const binding = bindings[key];
    if (shelfCycleSource !== undefined) {
      hints[key] = shelfCycleSource;
    } else if (SUPPRESS_UI_HINT_KEYS.has(String(key))) {
      hints[key] = "";
    } else {
      hints[key] = binding ? formatSfdcOnlyHint(binding) : "";
    }
  }
  return hints;
};

export const borderUsPlannedPrefillHints = buildHintMap(borderUsSfdcBindings, borderUsShelfCyclePrefillSources);
export const borderCanadaPlannedPrefillHints = buildHintMap(borderCanadaSfdcBindings, borderCanadaShelfCyclePrefillSources);
export const pickupInstructionsPlannedPrefillHints = buildHintMap(pickupInstructionsSfdcBindings, {
  carrierRates:
    "Freight costs from the sales order and linked purchase order are selected automatically; change the selection before create.",
  pickUpNotes: "Partially prefilled from PO supplier shipping instructions today.",
  includeBlindPickupDelivery: "Defaults from blind-ship order type today.",
  carrier: "Prefilled from shipping carrier on the order.",
  deliveryBlock: "Prefilled from ship-to address and shipping instructions.",
});
export const bolPlannedPrefillHints = buildHintMap(bolSfdcBindings, {
  carrier: "Prefilled from shipping carrier on the order.",
  lineCommodityDescription: "Prefilled from product hazmat fields and product code; editable before create.",
  linePieces: "Leave blank to calculate pieces from the final printed grouping. Enter a value to override it for this document.",
});
export const orderConfirmationPlannedPrefillHints = {
  ...buildHintMap(orderConfirmationSfdcBindings, {
    includeDrumHazardPage: "Check to add the optional drum hazard page 2 when org settings allow it.",
    orderConfirmationNote: "Prefilled from org default or shipping instructions on the order when blank.",
    lineDescription: "Prefilled from product data; refreshed from the order when the PDF is generated.",
    lineQuantityUnits: "Prefilled from package count × per-package qty in the line UOM when available; edit to override the UOM column.",
  }),
  supplierShippingRemarks: "Prefilled from linked PO supplier shipping remarks on direct-ship orders.",
};
